import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Store, ShoppingBag, Truck, Tag, Activity, AlertTriangle, Tv, History } from "lucide-react";
import AdminLayout from "layouts/AdminLayout";
import AdminCard from "components/admin/AdminCard";
import AdminStatCard from "components/admin/AdminStatCard";
import { Button } from "components/ui/button";
import { adminPaths } from "app/paths";
import DateRangeFilter, { DateRange, getDefaultDateRange } from "components/admin/DateRangeFilter";
import CustomAreaChart from "components/CustomAreaChart";
import CustomPieChart from "components/CustomPieChart";
import CustomBarChart from "components/CustomBarChart";
import WorldShipmentMap from "components/admin/WorldShipmentMap";
import ConversionFunnel from "components/admin/ConversionFunnel";
import {
  AdminDashboardSummary,
  AdminDashboardPeriodSummary,
  getAdminDashboardSummary,
  getAdminDashboardPeriodSummary,
  getEnviosPorDia,
  getCrescimentoMensal,
  getDistribuicaoTransportadora,
  getNovosClientes,
  getPedidosImportadosChart,
  getValorPorCliente,
  getEnviosPorPais,
  getFunilConversao,
  getSlaTransportadora,
  ValorPorClienteItem,
  PaisEnvios,
} from "services/admin/adminDashboard.service";
import { ChartPoint } from "services/admin/adminCommon.types";

const formatBRL = (value: number) => value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const formatUSD = (value: number) => value.toLocaleString("en-US", { style: "currency", currency: "USD" });

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<AdminDashboardSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [range, setRange] = useState<DateRange>(getDefaultDateRange());
  const [periodSummary, setPeriodSummary] = useState<AdminDashboardPeriodSummary | null>(null);
  const [enviosPorDia, setEnviosPorDia] = useState<ChartPoint[]>([]);
  const [crescimento, setCrescimento] = useState<ChartPoint[]>([]);
  const [distribuicao, setDistribuicao] = useState<ChartPoint[]>([]);
  const [novosClientes, setNovosClientes] = useState<ChartPoint[]>([]);
  const [pedidosImportados, setPedidosImportados] = useState<ChartPoint[]>([]);
  const [valorPorCliente, setValorPorCliente] = useState<ValorPorClienteItem[]>([]);
  const [dolar, setDolar] = useState<number | null>(null);
  const [enviosPorPais, setEnviosPorPais] = useState<PaisEnvios[]>([]);
  const [funil, setFunil] = useState<ChartPoint[]>([]);
  const [sla, setSla] = useState<Array<{ label: string; value: number; total: number }>>([]);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [chartsError, setChartsError] = useState<string | null>(null);

  useEffect(() => {
    getAdminDashboardSummary()
      .then(setSummary)
      .catch((err) => {
        console.error("[AdminDashboard] erro ao carregar resumo:", err);
        setSummaryError("Falha ao carregar os cards de resumo.");
      })
      .finally(() => setSummaryLoading(false));
  }, []);

  useEffect(() => {
    setChartsLoading(true);
    setChartsError(null);
    Promise.all([
      getAdminDashboardPeriodSummary(range),
      getEnviosPorDia(range),
      getCrescimentoMensal(range),
      getDistribuicaoTransportadora(range),
      getNovosClientes(range),
      getPedidosImportadosChart(range),
      getValorPorCliente(range),
      getEnviosPorPais(range),
      getFunilConversao(range),
      getSlaTransportadora(range),
    ])
      .then(([ps, e, c, d, n, p, v, pais, f, s]) => {
        setPeriodSummary(ps);
        setEnviosPorDia(e);
        setCrescimento(c);
        setDistribuicao(d);
        setNovosClientes(n);
        setPedidosImportados(p);
        setValorPorCliente(v.data);
        setDolar(v.dolar);
        setEnviosPorPais(pais);
        setFunil(f);
        setSla(s);
      })
      .catch((err) => {
        console.error("[AdminDashboard] erro ao carregar gráficos:", err);
        const status = err?.response?.status;
        const serverMsg = err?.response?.data?.error || err?.response?.data?.erro;
        setChartsError(
          status
            ? `Falha ao carregar gráficos (HTTP ${status})${serverMsg ? `: ${serverMsg}` : ""}`
            : err?.message || "Falha ao carregar gráficos do dashboard."
        );
      })
      .finally(() => setChartsLoading(false));
  }, [range]);

  return (
    <AdminLayout
      title="Dashboard"
      subtitle="Visão geral da operação Intrex"
      action={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(adminPaths.maquinaDoTempo)}>
            <History className="mr-2 h-4 w-4" />
            Máquina do Tempo
          </Button>
          <Button variant="outline" onClick={() => navigate(adminPaths.salaOperacoes)}>
            <Tv className="mr-2 h-4 w-4" />
            Modo TV
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {summaryError ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{summaryError}</div>
        ) : (
          <div>
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Estado atual</p>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              <AdminStatCard label="Clientes ativos" value={summaryLoading ? "—" : summary?.clientesAtivos ?? 0} icon={<Users />} />
              <AdminStatCard label="Lojas Shopify" value={summaryLoading ? "—" : summary?.lojasShopify ?? 0} icon={<Store />} />
              <AdminStatCard label="Lojas Nuvemshop" value={summaryLoading ? "—" : summary?.lojasNuvemshop ?? 0} icon={<ShoppingBag />} />
              <AdminStatCard label="Envios hoje" value={summaryLoading ? "—" : summary?.enviosHoje ?? 0} icon={<Truck />} />
              <AdminStatCard
                label="APIs online"
                value={summaryLoading ? "—" : `${summary?.apisOnline ?? 0}/${summary?.totalIntegracoes ?? 0}`}
                icon={<Activity />}
                tone={
                  !summaryLoading && (summary?.apisOnline ?? 0) < (summary?.totalIntegracoes ?? 0) ? "danger" : "success"
                }
              />
              <AdminStatCard
                label="Integrações com erro"
                value={summaryLoading ? "—" : summary?.integracoesComErro ?? 0}
                icon={<AlertTriangle />}
                tone={!summaryLoading && (summary?.integracoesComErro ?? 0) > 0 ? "danger" : "default"}
              />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-[13px] font-semibold text-slate-500 uppercase tracking-wide">Período selecionado</h2>
          <DateRangeFilter onChange={setRange} />
        </div>

        {chartsError ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{chartsError}</div>
        ) : chartsLoading ? (
          <p className="text-sm text-muted-foreground">Carregando...</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <AdminStatCard label="Envios no período" value={periodSummary?.enviosNoPeriodo ?? 0} icon={<Truck />} />
              <AdminStatCard label="Etiquetas emitidas" value={periodSummary?.etiquetasEmitidas ?? 0} icon={<Tag />} />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <AdminCard title="Envios por dia">
                <CustomAreaChart data={enviosPorDia} />
              </AdminCard>
              <AdminCard title="Crescimento mensal de clientes">
                <CustomBarChart
                  data={crescimento}
                  xKey="label"
                  series={[{ key: "value", label: "Novos clientes", color: "#2563EB" }]}
                />
              </AdminCard>
              <AdminCard title="UPS x FedEx">
                <CustomPieChart data={distribuicao} />
              </AdminCard>
              <AdminCard title="Novos clientes">
                <CustomAreaChart data={novosClientes} />
              </AdminCard>
              <AdminCard title="Pedidos importados" className="xl:col-span-2">
                <CustomBarChart
                  data={pedidosImportados}
                  xKey="label"
                  series={[{ key: "value", label: "Pedidos", color: "#90AEEF" }]}
                />
              </AdminCard>
            </div>

            <AdminCard
              title="Top clientes por valor enviado"
              action={dolar ? <span className="text-xs text-slate-400">Dólar: {formatBRL(dolar)}</span> : undefined}
            >
              {valorPorCliente.length ? (
                <ul className="flex flex-col divide-y divide-slate-100">
                  {valorPorCliente.map((c) => (
                    <li key={c.clienteId} className="flex items-center justify-between text-sm py-2.5 first:pt-0 last:pb-0">
                      <div>
                        <p className="font-medium text-slate-800">{c.label}</p>
                        <p className="text-xs text-slate-400">{c.envios} envio{c.envios === 1 ? "" : "s"}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">{c.value != null ? formatBRL(c.value) : "—"}</p>
                        <p className="text-xs text-slate-400">{formatUSD(c.totalUsd)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">Nenhum envio no período selecionado.</p>
              )}
            </AdminCard>

            <AdminCard title="Envios por país">
              <WorldShipmentMap data={enviosPorPais} />
            </AdminCard>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <AdminCard title="Funil de conversão">
                <ConversionFunnel data={funil} />
              </AdminCard>
              <AdminCard title="SLA por transportadora (dias médios até entrega)">
                {sla.length ? (
                  <CustomBarChart
                    data={sla}
                    xKey="label"
                    series={[{ key: "value", label: "Dias médios", color: "#2563EB" }]}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">Nenhuma entrega concluída no período selecionado.</p>
                )}
              </AdminCard>
            </div>
          </>
        )}

        <AdminCard title="Últimas sincronizações">
          {summary?.ultimasSincronizacoes?.length ? (
            <ul className="flex flex-col divide-y divide-slate-100">
              {summary.ultimasSincronizacoes.map((log) => (
                <li key={log.id} className="flex items-center justify-between text-sm py-2.5 first:pt-0 last:pb-0">
                  <span className="flex items-center gap-2.5">
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        log.status === "ok" ? "bg-green-500" : "bg-red-500"
                      }`}
                    />
                    <span className="font-medium text-slate-700 uppercase text-xs tracking-wide">
                      {log.integration}
                    </span>
                    <span className="text-slate-500">{log.message}</span>
                  </span>
                  <span className="text-slate-400 text-xs">{new Date(log.createdAt).toLocaleString("pt-BR")}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhuma sincronização registrada ainda.</p>
          )}
        </AdminCard>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
